import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { togglePopover } from "../../redux/Slices/popoverSlice";
import useHandlePopoverClick from "../../hooks/useHandlePopoverClick";
import "./scss/MemberRole.scss";
import { config } from "../../config";
import { fetchMembers, optimisticUpdateMemberRole } from "../../redux/Slices/memberSlice";
import { showSuccess, showError } from "../../utils/snackbarHelpers";
import { textTransform } from "../../utils/helpers";

const MemberRole = (props) => {
    const {
        memberId,
        currentRole,
        memberDeleteTarget,
        totalAdmins,
        apiPath,
        typeId,
        canChangeRole = true, // Default to true for backward compatibility
        isWorkspaceAdmin = false, // Workspace admins bypass last-admin restriction
    } = props;

    const dispatch = useDispatch();
    const { handlePopoverClick } = useHandlePopoverClick();
    const { user } = useSelector((state) => state.user);
    const workspaceMembers = useSelector(state => state.member.workspaceMembers);
    const workspaceGuests = useSelector(state => state.member.workspaceGuests);
    const boardMembers = useSelector(state => state.member.boardMembers);

    // State for confirmation screen
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingRole, setPendingRole] = useState(null);

    // Determine API path based on the type
    const apiPathMap = {
        board: "boards",
        workspace: "workspace",
    };
    const _apiPath = apiPathMap[apiPath] || "workspace";

    // Role options for members
    const roles = apiPath === 'workspace' ? [
        {
            value: "admin",
            label: "Admin",
            description: "Admins must have a confirmed email address.",
        },
        { value: "member", label: "Member" },
        {
            value: "guest",
            label: "Guest",
            description: "Guests can only access specific boards they're invited to."
        },
    ] : [
        {
            value: "admin",
            label: "Admin",
            description: "Admins must have a confirmed email address.",
        },
        { value: "member", label: "Member" },
        {
            value: "observer",
            label: "Observer",
            description: "Can view and comment on this board, but not make changes.",
        },
    ];

    // Function to update the member's role
    const updateMemberRole = async (newRole, memberId, type, typeId) => {
        if (!typeId || !memberId) {
            showError(dispatch, `${type} Id or Member Id is empty`);
            return;
        }

        try {
            const payload = {
                memberId,
                newRole: newRole.value,
                typeId
            };

            const response = await fetch(
                `${config.API_URI}/v1/${_apiPath}/${typeId}/member`,
                {
                    method: "PUT",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    credentials: 'include',
                    body: JSON.stringify(payload),
                }
            );

            const data = await response.json();
            const messageType = response.status === 200 ? "success" : "error";

            if (messageType === 'success') {
                showSuccess(dispatch, data.message);
                dispatch(
                    togglePopover({
                        contentId: null,
                        position: { top: 0, left: 0 },
                        targetId: null,
                    })
                );

                // Refresh member lists after successful role update
                if (type === 'workspace') {
                    dispatch(fetchMembers({ type: 'workspace', id: typeId }));
                    dispatch(fetchMembers({ type: 'guests', id: typeId }));
                } else {
                    dispatch(fetchMembers({ type, id: typeId }));
                }
            } else {
                showError(dispatch, data.message);
            }
        } catch (error) {
            console.error("Error updating member role:", error);
            showError(dispatch, "Error updating member role");
        }
    };

    // Handle role change with confirmation check
    const handleRoleChange = (newRole) => {
        // Check if current user is changing their own role from admin to non-admin
        const isSelfChange = user?.user?._id === memberId;
        const isAdminDowngrade = currentRole === 'admin' && newRole.value !== 'admin';

        if (isSelfChange && isAdminDowngrade) {
            // Show confirmation screen
            setPendingRole(newRole);
            setShowConfirmation(true);
        } else {
            // Directly update role
            updateMemberRole(newRole, memberId, apiPath, typeId);
        }
    };

    // Handle confirmation of admin downgrade
    const handleConfirmDowngrade = () => {
        if (pendingRole) {
            updateMemberRole(pendingRole, memberId, apiPath, typeId);
            setShowConfirmation(false);
            setPendingRole(null);
        }
    };

    // Handle cancellation of admin downgrade
    const handleCancelDowngrade = () => {
        setShowConfirmation(false);
        setPendingRole(null);
    };


    // Render role options
    const renderRoleOptions = () =>
        roles.map((role) => {
            const isAdminRestricted = totalAdmins < 2 && currentRole === "admin" && !isWorkspaceAdmin;
            const isRoleChangeRestricted = !canChangeRole && currentRole !== role.value;
            const isDisabled = isAdminRestricted || currentRole === role.value || isRoleChangeRestricted;

            return (
                <div
                    key={role.value}
                    className={`popover-member-role popover-option ${currentRole === role.value ? "selected" : ""
                        } ${isDisabled ? "remove-disabled" : ""}`}
                    onClick={() => {
                        if (isDisabled || currentRole === role.value) return;
                        handleRoleChange(role);
                    }}
                >
                    <span className="option-title">{role.label}</span>
                    {role.description && !isRoleChangeRestricted && (
                        <span className="option-description">{role.description}</span>
                    )}
                    {isRoleChangeRestricted && (
                        <span className="option-description">
                            Only {apiPath} admins can change member roles.
                        </span>
                    )}

                    {/* {isAdminRestricted < 2 && (
                        <span className="option-description">
                            Boards must have at least one admin.
                        </span>
                    )} */}

                </div>
            );
        });

    // Render remove/leave option
    const renderRemoveOrLeaveOption = () => {
        const targetElement = document.getElementById(memberDeleteTarget);
        const apiUrl = `${config.API_URI}/v1/${_apiPath}/${typeId}/member`;

        const isSelfRemoval = user?.user?._id === memberId;
        const isAdminRestricted = totalAdmins < 2 && currentRole === "admin" && !isWorkspaceAdmin;

        // For workspace: Only admins can remove other members, but anyone can leave (remove themselves)
        // For board: canChangeRole determines if user can remove others
        const canRemoveOthers = apiPath === 'workspace' ? canChangeRole : canChangeRole;
        const isRemovalRestricted = !isSelfRemoval && !canRemoveOthers;

        return (
            <div
                className={`popover-member-role popover-option ${isAdminRestricted || isRemovalRestricted ? "remove-disabled" : ""}`}
                onClick={() => {
                    if (isAdminRestricted || isRemovalRestricted) return;

                    handlePopoverClick(targetElement, "deleteAction", {
                        type: "MEMBER",
                        id: memberId,
                        url: apiUrl,
                        apiPath: _apiPath === 'boards' ? 'board' : 'workspace',
                        typeId,
                    });
                }}
            >
                {isSelfRemoval ? (
                    <>
                        <span className="option-title">Leave {apiPath}</span>
                        {isAdminRestricted && (
                            <span className="option-description">
                                {textTransform(apiPath, 'capitalize')}s must have at least one admin.
                            </span>
                        )}
                    </>
                ) : (
                    <>
                        <span className="option-title">Remove from {apiPath}</span>
                        {isAdminRestricted && (
                            <span className="option-description">
                                {textTransform(apiPath, 'capitalize')}s must have at least one admin.
                            </span>
                        )}
                        {isRemovalRestricted && (
                            <span className="option-description">
                                Only {apiPath} admins can remove members.
                            </span>
                        )}
                    </>
                )}
            </div>
        );
    };


    // Render confirmation screen
    if (showConfirmation) {
        return (
            <div className="member-role-container">
                <div className="member-role-confirmation">
                    <h3>Change Role?</h3>
                    <p className="warning-message">
                        You are about to remove your admin privileges. You will no longer be able to manage {apiPath} members, change roles, or invite new members.
                    </p>
                    <p className="confirmation-question">
                        Are you sure you want to continue?
                    </p>
                    <div className="confirmation-actions">
                        <button className="cancel-button" onClick={handleCancelDowngrade}>
                            Cancel
                        </button>
                        <button className="confirm-button" onClick={handleConfirmDowngrade}>
                            Change Role
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="member-role-container">
            {renderRoleOptions()}
            {renderRemoveOrLeaveOption()}
        </div>
    );
};

export default MemberRole;
